// Sentry error tracking (frontend). Errors only — no performance tracing, no
// session replay — so there is no runtime overhead and the free-tier quota
// lasts. Initialized first thing in main.tsx; silent in dev and when no DSN
// is configured.
//
// Privacy posture (childcare data): no default PII, breadcrumb URLs are
// stripped of query strings (signed storage URLs carry access tokens), and
// the user context is the account UUID only — enough to correlate an error
// with the App Versions tracker, never a name or email.
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { getAppVersionInfo } from "@/hooks/use-app-version-tracker";

const stripQuery = (url: string | undefined): string | undefined => {
  if (!url) return url;
  const i = url.indexOf("?");
  return i === -1 ? url : url.slice(0, i);
};

// Signing links carry their credential in the PATH, not the query: /cosign/:token
// is a 14-day invitation and /sign/:token a handoff to a phone. Stripping the
// query alone left the whole token in navigation breadcrumbs and in the error
// event's own URL, which would hand anyone with Sentry access one of the two
// factors needed to sign a registration form as somebody's parent.
const redactSigningTokens = (url: string | undefined): string | undefined =>
  url?.replace(/[/](cosign|sign)[/][^/?#]+/gi, "/$1/[token]");

/** Query stripped and signing tokens redacted. */
const scrubUrl = (url: string | undefined): string | undefined =>
  redactSigningTokens(stripQuery(url));

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn || !import.meta.env.PROD) return;

  Sentry.init({
    dsn,
    // Same hash the App Versions tracker shows, so every error names the
    // build that produced it.
    release: getAppVersionInfo().commitHash,
    environment: "production",
    sendDefaultPii: false,
    maxBreadcrumbs: 30,
    tracesSampleRate: 0,
    // Cross-browser noise that never indicates a defect in our code.
    ignoreErrors: [
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      "AbortError",
      "TypeError: Failed to fetch",
      "TypeError: Load failed",
      "TypeError: NetworkError when attempting to fetch resource",
      // Facebook/Instagram in-app browser telemetry: its native JS bridge is
      // torn down before its own beforeunload handler finishes, so the bridge
      // call throws. Nothing to do with our code and invisible to the user.
      "Java object is gone",
      // Browser-extension content scripts messaging a tab whose context is
      // gone (runtime.sendMessage is the WebExtension API, not ours). These
      // rejections arrive with no stack frames, so the extension denyUrls
      // below never get a URL to match — only the message can catch them.
      "Invalid call to runtime.sendMessage",
      // Facebook's iOS in-app browser probing its native bridge
      // (window.webkit.messageHandlers) from an injected script. Sibling of
      // the Android "Java object is gone" above.
      "evaluating 'window.webkit.messageHandlers'",
      // An injected script (extension or iOS content blocker) referencing its
      // own missing global; the symbol appears nowhere in our code or deps,
      // and the events carry no usable stack to deny by URL.
      "Can't find variable: EmptyRanges",
    ],
    denyUrls: [
      /extensions\//i,
      // In-app browser injected scripts (Facebook/Instagram webviews).
      /^iabjs:\/\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /safari-(web-)?extension:/i,
    ],
    beforeBreadcrumb(breadcrumb) {
      // Signed media URLs put their access token in the query string — keep
      // the path (diagnostic) and drop the query (secret).
      if ((breadcrumb.category === "fetch" || breadcrumb.category === "xhr") && breadcrumb.data) {
        breadcrumb.data.url = scrubUrl(breadcrumb.data.url as string | undefined);
      }
      if (breadcrumb.category === "navigation" && breadcrumb.data) {
        breadcrumb.data.from = scrubUrl(breadcrumb.data.from as string | undefined);
        breadcrumb.data.to = scrubUrl(breadcrumb.data.to as string | undefined);
      }
      return breadcrumb;
    },
    beforeSend(event) {
      // The event carries the page's own URL in two more places than the
      // breadcrumbs do.
      if (event.request?.url) event.request.url = scrubUrl(event.request.url);
      if (typeof event.transaction === "string") {
        event.transaction = redactSigningTokens(event.transaction);
      }
      return event;
    },
  });

  supabase.auth.onAuthStateChange((_event, session) => {
    Sentry.setUser(session?.user ? { id: session.user.id } : null);
  });
}
